import { useEffect, useCallback } from 'react';

/**
 * Advanced Caching Utilities for Historical Reports
 * 
 * Provides intelligent caching with TTL, LRU eviction, and cache invalidation
 */

interface CacheEntry<T> {
    data: T;
    timestamp: number;
    accessCount: number;
    lastAccessed: number;
    ttl?: number;
}

interface CacheOptions {
    maxSize?: number;
    defaultTTL?: number; // Time to live in milliseconds
    onEvict?: (key: string, entry: CacheEntry<any>) => void;
}

export class LRUCache<T> {
    private cache = new Map<string, CacheEntry<T>>();
    private maxSize: number;
    private defaultTTL: number;
    private onEvict?: (key: string, entry: CacheEntry<T>) => void;

    constructor(options: CacheOptions = {}) {
        this.maxSize = options.maxSize || 100;
        this.defaultTTL = options.defaultTTL || 5 * 60 * 1000; // 5 minutes default
        this.onEvict = options.onEvict;
    }

    set(key: string, data: T, ttl?: number): void {
        const now = Date.now();
        const entry: CacheEntry<T> = {
            data,
            timestamp: now,
            accessCount: 0,
            lastAccessed: now,
            ttl: ttl || this.defaultTTL,
        };

        // Remove existing entry if present
        if (this.cache.has(key)) {
            this.cache.delete(key);
        }

        // Add new entry
        this.cache.set(key, entry);

        // Evict if over capacity
        if (this.cache.size > this.maxSize) {
            this.evictLRU();
        }
    }

    get(key: string): T | null {
        const entry = this.cache.get(key);
        if (!entry) return null;

        // Check if expired
        if (this.isExpired(entry)) {
            this.cache.delete(key);
            this.onEvict?.(key, entry);
            return null;
        }

        // Update access info
        entry.accessCount++;
        entry.lastAccessed = Date.now();

        // Move to end (most recently used)
        this.cache.delete(key);
        this.cache.set(key, entry);

        return entry.data;
    }

    has(key: string): boolean {
        const entry = this.cache.get(key);
        if (!entry) return false;

        if (this.isExpired(entry)) {
            this.cache.delete(key);
            this.onEvict?.(key, entry);
            return false;
        }

        return true;
    }

    delete(key: string): boolean {
        const entry = this.cache.get(key);
        if (entry) {
            this.onEvict?.(key, entry);
        }
        return this.cache.delete(key);
    }

    clear(): void {
        this.cache.forEach((entry, key) => {
            this.onEvict?.(key, entry);
        });
        this.cache.clear();
    }

    invalidatePattern(pattern: RegExp): number {
        let removed = 0;
        for (const [key, entry] of this.cache.entries()) {
            if (pattern.test(key)) {
                this.cache.delete(key);
                this.onEvict?.(key, entry);
                removed++;
            }
        }
        return removed;
    }

    cleanup(): number {
        let removed = 0;
        for (const [key, entry] of this.cache.entries()) {
            if (this.isExpired(entry)) {
                this.cache.delete(key);
                this.onEvict?.(key, entry);
                removed++;
            }
        }
        return removed;
    }

    getStats() {
        let totalSize = 0;
        let expiredCount = 0;
        let totalAccess = 0;

        for (const entry of this.cache.values()) {
            totalSize++;
            totalAccess += entry.accessCount;
            if (this.isExpired(entry)) {
                expiredCount++;
            }
        }

        return {
            size: totalSize,
            maxSize: this.maxSize,
            expiredCount,
            averageAccess: totalAccess / Math.max(totalSize, 1),
            memoryUsage: this.estimateMemoryUsage(),
        };
    }

    private isExpired(entry: CacheEntry<T>): boolean {
        if (!entry.ttl) return false;
        return Date.now() - entry.timestamp > entry.ttl;
    }

    private evictLRU(): void {
        // Remove least recently used item
        const firstEntry = this.cache.entries().next();
        if (!firstEntry.done) {
            const [firstKey, entry] = firstEntry.value;
            this.cache.delete(firstKey);
            this.onEvict?.(firstKey, entry);
        }
    }

    private estimateMemoryUsage(): number {
        // Rough estimation in KB
        return this.cache.size * 2; // 2KB per entry estimate
    }
}

/**
 * Cache key generation utilities
 */
export const CacheKeys = {
    reportsList: (filters: Record<string, any>, offset: number, limit: number) => {
        const filterStr = Object.entries(filters)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([k, v]) => `${k}:${v}`)
            .join('|');
        return `reports:list:${filterStr}:${offset}:${limit}`;
    },

    reportDetail: (reportId: string) => `reports:detail:${reportId}`,

    userReports: (userEmail: string) => `reports:user:${userEmail}`,

    folderReports: (folderId: string) => `reports:folder:${folderId}`,
} as const;

/**
 * Cache invalidation patterns
 */
export const CachePatterns = {
    allReports: /^reports:/,
    userReports: (userEmail: string) => new RegExp(`^reports:.*:.*user_email:${userEmail}`),
    folderReports: (folderId: string) => new RegExp(`^reports:.*folder_id:${folderId}`),
} as const;

/**
 * React Hook for cache management
 */
export function useCache<T>(
    cacheInstance: LRUCache<T>,
    options: {
        cleanupInterval?: number;
        onCacheHit?: (key: string) => void;
        onCacheMiss?: (key: string) => void;
    } = {}
) {
    const { cleanupInterval = 60000, onCacheHit, onCacheMiss } = options; // 1 minute cleanup

    // Periodic cleanup
    useEffect(() => {
        const interval = setInterval(() => {
            const removed = cacheInstance.cleanup();
            if (removed > 0) {
                // Cache cleanup removed expired entries
            }
        }, cleanupInterval);

        return () => clearInterval(interval);
    }, [cacheInstance, cleanupInterval]);

    const get = useCallback((key: string): T | null => {
        const result = cacheInstance.get(key);
        if (result !== null) {
            onCacheHit?.(key);
        } else {
            onCacheMiss?.(key);
        }
        return result;
    }, [cacheInstance, onCacheHit, onCacheMiss]);

    const set = useCallback((key: string, data: T, ttl?: number) => {
        cacheInstance.set(key, data, ttl);
    }, [cacheInstance]);

    const invalidate = useCallback((pattern: RegExp) => {
        return cacheInstance.invalidatePattern(pattern);
    }, [cacheInstance]);

    const clear = useCallback(() => {
        cacheInstance.clear();
    }, [cacheInstance]);

    const stats = useCallback(() => {
        return cacheInstance.getStats();
    }, [cacheInstance]);

    return { get, set, invalidate, clear, stats };
}

/**
 * Request deduplication utility
 */
export class RequestDeduplicator<T> {
    private pendingRequests = new Map<string, Promise<T>>();

    async dedupe(key: string, requestFn: () => Promise<T>): Promise<T> {
        // Return existing promise if request is already pending
        if (this.pendingRequests.has(key)) {
            return this.pendingRequests.get(key)!;
        }

        // Start new request
        const promise = requestFn()
            .finally(() => {
                // Remove from pending when completed
                this.pendingRequests.delete(key);
            });

        this.pendingRequests.set(key, promise);
        return promise;
    }

    clear(): void {
        this.pendingRequests.clear();
    }

    hasPending(key: string): boolean {
        return this.pendingRequests.has(key);
    }

    getPendingCount(): number {
        return this.pendingRequests.size;
    }
}

/**
 * Global cache instances for historical reports
 */
export const reportListCache = new LRUCache<any>({
    maxSize: 50,
    defaultTTL: 2 * 60 * 1000, // 2 minutes for list data
    // onEvict: (key) => {},
});

export const reportDetailCache = new LRUCache<any>({
    maxSize: 100,
    defaultTTL: 10 * 60 * 1000, // 10 minutes for detail data
    // onEvict: (key) => {},
});

export const requestDeduplicator = new RequestDeduplicator();